import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
//import axios from 'axios';
import { getTasks } from './services/tasksService';
import './App.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

//const API_BASE = 'http://localhost:5000/api';
const NEON_GREEN = '#00ff88';
const NEON_PURPLE = '#8b5cf6';
const NEON_RED = '#ef4444';
const NEON_ORANGE = '#f97316';
const NEON_BG = '#0a0a0a';

function Dashboard() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    overdueTasks: 0,
    completionRate: 0,
    currentStreak: 0,
    averagePriority: 0,
    tasksCompletedToday: 0
  });
  const [weeklyData, setWeeklyData] = useState([]);
  const [priorityData, setPriorityData] = useState([]);
  const [pendingByImportance, setPendingByImportance] = useState([]); // New state for pending tasks by importance
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date()); // Tracks current calendar month

  useEffect(() => {
    if (user && user.name !== username) {
      navigate(`/dashboard/${user.name}`, { replace: true });
    }
  }, [user, username, navigate]);

 // useEffect(() => {
 //   fetchDashboardData();
  //}, []);
  useEffect(() => {
  if (user) {
    fetchDashboardData();
  }
}, [user]);

 const fetchDashboardData = async () => {
  try {
    const tasksData = await getTasks(); // from Firebase Firestore
    setTasks(tasksData);

    // Calculate statistics
    calculateStats(tasksData);
    calculateWeeklyData(tasksData);
    calculatePriorityData(tasksData);
    calculatePendingByImportance(tasksData);

    setLoading(false);
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    setLoading(false);
  }
};


  const calculateStats = (tasksData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const totalTasks = tasksData.length;
    const completedTasks = tasksData.filter(task => task.completed).length;
    const pendingTasks = totalTasks - completedTasks;
    const overdueTasks = tasksData.filter(task => {
      if (task.completed || !task.dueDate) return false;
      
      const taskDueDate = new Date(task.dueDate);
      // ADD THIS VALIDATION:
      if (isNaN(taskDueDate.getTime())) return false;
      
      return taskDueDate < today;
    }).length;
    
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const averagePriority = tasksData.length > 0 
      ? Math.round(tasksData.reduce((sum, task) => sum + task.importance, 0) / tasksData.length)
      : 0;
    
    const tasksCompletedToday = tasksData.filter(task => {
      if (!task.completed || !task.completedAt) return false;
      const completedDate = new Date(task.completedAt);
      return completedDate >= today;
    }).length;

    // Calculate current streak
    const currentStreak = calculateStreak(tasksData);

    setStats({
      totalTasks,
      completedTasks,
      pendingTasks,
      overdueTasks,
      completionRate,
      currentStreak,
      averagePriority,
      tasksCompletedToday
    });
  };
  const calculateStreak = (tasksData) => {
    // 1. Filter for completed tasks that have a completedAt timestamp
    const completedTasks = tasksData.filter(task => task.completed && task.completedAt);
    
    if (completedTasks.length === 0) return 0;

    // 2. Robustly convert all completedAt timestamps to valid Date objects
    const validCompletedDates = completedTasks
      .map(task => {
        // NOTE: If completedAt is a Firestore Timestamp object, 
        // use new Date(task.completedAt.toDate()) or similar conversion.
        // Assuming it's a string/timestamp value that can be parsed:
        return new Date(task.completedAt);
      })
      // Filter out any dates that failed to parse (i.e., RangeError prevention)
      .filter(d => !isNaN(d.getTime())); 

    if (validCompletedDates.length === 0) return 0;

    // 3. Normalize dates to the start of the day (midnight) and extract unique days
    const completedDays = new Set(
      validCompletedDates.map(date => {
        date.setHours(0, 0, 0, 0); // Normalize to midnight
        return date.getTime(); // Use milliseconds for easy comparison
      })
    );

    // 4. Convert Set back to a sorted array of timestamps (most recent first)
    const sortedCompletedDays = Array.from(completedDays).sort((a, b) => b - a);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    let streak = 0;
    let expectedDate = todayTime;

    // Check if today has completions (start the streak)
    if (completedDays.has(todayTime)) {
      streak = 1;
      // Move expected date to yesterday for the next check
      expectedDate = todayTime - 24 * 60 * 60 * 1000;
    } else {
      // If nothing was done today, check for the most recent day in the past.
      // This ensures we continue the streak if the last completed task was yesterday.
      
      // Look for yesterday's timestamp
      const yesterdayTime = todayTime - 24 * 60 * 60 * 1000;
      if (completedDays.has(yesterdayTime)) {
          streak = 1;
          expectedDate = yesterdayTime - 24 * 60 * 60 * 1000;
      } else {
          // No completions today or yesterday, streak is 0
          return 0; 
      }
    }


    // 5. Iterate backwards through days to count the streak
    while (true) {
      // Check if the expectedDate (yesterday, day before, etc.) is in the completed set
      if (completedDays.has(expectedDate)) {
        streak++;
        // Move back one day
        expectedDate -= 24 * 60 * 60 * 1000;
      } else {
        break;
      }
      
      // Optional safety break for extremely long streaks (e.g., limit to 365 days)
      if (streak > 365) break; 
    }

    return streak;
  };

  const calculateWeeklyData = (tasksData) => {
    const weekData = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      const dayTasks = tasksData.filter(task => {
        if (!task.completedAt) return false;
        const taskDate = new Date(task.completedAt);
        return taskDate >= date && taskDate < nextDate;
      });
      
      weekData.push({
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        completed: dayTasks.length,
        date: date
      });
    }
    
    setWeeklyData(weekData);
  };

  const calculatePriorityData = (tasksData) => {
    const priorityCounts = {};
    
    tasksData.forEach(task => {
      const priority = task.importance;
      priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
    });
    
    const priorityArray = Object.entries(priorityCounts).map(([priority, count]) => ({
      priority: parseInt(priority),
      count: count
    }));
    
    setPriorityData(priorityArray);
  };

  const calculatePendingByImportance = (tasksData) => {
    const pendingCounts = {};
    // Filter for pending tasks (not completed)
    const pendingTasks = tasksData.filter(task => !task.completed);
    
    // Initialize counts for all importance levels (1-10)
    for (let i = 1; i <= 10; i++) {
      pendingCounts[i] = 0;
    }
    
    // Count pending tasks by importance
    pendingTasks.forEach(task => {
      const priority = task.importance || 0; // Default to 0 if undefined
      if (priority >= 1 && priority <= 10) {
        pendingCounts[priority] = (pendingCounts[priority] || 0) + 1;
      }
    });
    
    // Convert to array for recharts
    const pendingData = Object.entries(pendingCounts).map(([priority, count]) => ({
      importance: parseInt(priority),
      tasks: count
    }));
    
    setPendingByImportance(pendingData);
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'var(--danger-red)';
    if (priority >= 6) return 'var(--warning-orange)';
    return 'var(--success-green)';
  };

  const getCompletionRateColor = (rate) => {
    if (rate >= 80) return 'var(--success-green)';
    if (rate >= 60) return 'var(--warning-orange)';
    return 'var(--danger-red)';
  };

  // Helper to format date in IST, DD/MM/YYYY, 24-hour
  // Helper to format date in IST, DD/MM/YYYY, 24-hour
const formatISTDateTime = (dateString) => {
  if (!dateString) return '—';
  
  // NOTE: Firebase Timestamps, when converted to JS objects, 
  // sometimes need .toDate() called on them first, but assuming 
  // they are strings here:
  const date = new Date(dateString);
  
  // CRITICAL FIX: Ensure the created Date object is valid
  if (isNaN(date.getTime())) return '—';
  
  // If the date is valid, proceed with formatting.
  // The rest of your IST conversion logic is fine.
  
  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffset);
  const day = String(istDate.getUTCDate()).padStart(2, '0');
  const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
  const year = istDate.getUTCFullYear();
  const hours = String(istDate.getUTCHours()).padStart(2, '0');
  const minutes = String(istDate.getUTCMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

  // Helper: Get day of week in Indian English
  const getDayName = (date) => {
    return date.toLocaleDateString('en-IN', { weekday: 'long' });
  };

  // Helper: Get productivity insights for last 7 days
  const getProductivityInsights = () => {
    if (!tasks.length) return null;
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const recentTasks = tasks.filter(task => task.completed && task.completedAt && new Date(task.completedAt) >= sevenDaysAgo);
    if (!recentTasks.length) return null;

    // Count completions by day of week in the last 7 days
    const dayCounts = Array(7).fill(0);
    recentTasks.forEach(task => {
      const d = new Date(task.completedAt);
      dayCounts[d.getDay()]++;
    });
    const maxCount = Math.max(...dayCounts);
    const minCount = Math.min(...dayCounts);
    const mostProductiveDayIdx = dayCounts.indexOf(maxCount);
    const leastProductiveDayIdx = dayCounts.indexOf(minCount);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // Weekday vs weekend in the last 7 days
    const weekdaySum = dayCounts.slice(1, 6).reduce((a, b) => a + b, 0);
    const weekendSum = dayCounts[0] + dayCounts[6];
    let trendMsg = '';
    if (weekdaySum > weekendSum) {
      trendMsg = 'You are more productive on weekdays than weekends in the last 7 days.';
    } else if (weekendSum > weekdaySum) {
      trendMsg = 'You are more productive on weekends than weekdays in the last 7 days.';
    } else {
      trendMsg = 'Your productivity is balanced across the week in the last 7 days.';
    }
    return {
      most: days[mostProductiveDayIdx],
      least: days[leastProductiveDayIdx],
      trendMsg
    };
  };

  // Helper: Get top N days with completion counts
  const getTopNDays = (n, order = 'desc') => {
    const dateMap = {};
    tasks.forEach(task => {
      if (task.completed && task.completedAt) {
        // Use a simple date format that works for grouping
        const d = new Date(task.completedAt);
        const dateStr = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
        dateMap[dateStr] = (dateMap[dateStr] || 0) + 1;
      }
    });

    const arr = Object.entries(dateMap).map(([date, count]) => ({ date, count }));
    
    // Sort strictly by task count
    // descending for 'most productive', ascending for 'least productive'
    return arr
      .sort((a, b) => order === 'desc' ? b.count - a.count : a.count - b.count)
      .slice(0, n);
  };
  // Weekend vs Weekday
  const getWeekendWeekdayStats = () => {
    let weekday = 0, weekend = 0;
    tasks.forEach(task => {
      if (task.completed && task.completedAt) {
        const d = new Date(task.completedAt);
        const day = d.getDay();
        if (day === 0 || day === 6) weekend++;
        else weekday++;
      }
    });
    return { weekday, weekend };
  };

  // Prepare data for recharts
  const lineChartData = weeklyData.map(day => ({
    name: day.day,
    Completed: day.completed
  }));
  
  const pieData = [
    { name: 'Completed', value: stats.completedTasks },
    { name: 'Pending', value: stats.pendingTasks },
    { name: 'Overdue', value: stats.overdueTasks }
  ];
  const pieColors = [NEON_GREEN, NEON_PURPLE, NEON_RED];
  // Helper to format the X-Axis dates to dd-mm-yy
  const formatXAxisDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year.slice(-2)}`;
  };

  // Most/least productive 7 days
  const most7 = getTopNDays(7, 'desc').map(item => ({
    ...item,
    formattedDate: formatXAxisDate(item.date)
  }));

  const least7 = getTopNDays(7, 'asc').map(item => ({
    ...item,
    formattedDate: formatXAxisDate(item.date)
  }));

  // Weekend vs Weekday
  const weekendWeekday = getWeekendWeekdayStats();
  const weekendWeekdayPie = [
    { name: 'Weekdays', value: weekendWeekday.weekday },
    { name: 'Weekends', value: weekendWeekday.weekend }
  ];

  // Prepare data for calendar heatmap
// Prepare data for calendar heatmap
// 🌟 NEW: Calculate categorization for heatmap
  const dateMap = {};
  tasks.filter(t => t.completed && t.completedAt).forEach(t => {
    const d = new Date(t.completedAt);
    if (isNaN(d.getTime())) return;
    
    const dateStr = d.toISOString().slice(0, 10);
    if (!dateMap[dateStr]) {
      dateMap[dateStr] = { onTime: 0, late: 0, total: 0 };
    }

    const dueDate = t.dueDate ? new Date(t.dueDate) : null;
    // Reset hours to compare dates only
    const compDate = new Date(d).setHours(0,0,0,0);
    const dueTime = dueDate ? new Date(dueDate).setHours(0,0,0,0) : Infinity;

    if (compDate <= dueTime) {
      dateMap[dateStr].onTime += 1;
    } else {
      dateMap[dateStr].late += 1;
    }
    dateMap[dateStr].total += 1;
  });

  const calendarData = Object.entries(dateMap).map(([date, stats]) => ({
    date,
    ...stats
  }));
  // For calendar range
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 180);
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }
    // --- Add this inside Dashboard component above the return ---
const getCalendarDays = () => {
    const days = [];
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    
    const startMonth = new Date(year, month, 1);
    const endMonth = new Date(year, month + 1, 0);
    
    for (let i = 0; i < startMonth.getDay(); i++) {
      days.push({ empty: true });
    }

    for (let d = 1; d <= endMonth.getDate(); d++) {
      const currentLoopDate = new Date(year, month, d);
      const dateStr = currentLoopDate.toISOString().slice(0, 10);
      const dayData = dateMap[dateStr] || { onTime: 0, late: 0, total: 0 };
      
      const prevDateStr = new Date(year, month, d - 1).toISOString().slice(0, 10);
      const hasConnection = dayData.onTime > 0 && dateMap[prevDateStr]?.onTime > 0;

      days.push({
        day: d,
        date: dateStr,
        ...dayData,
        hasConnection
      });
    }
    return days;
  };

  const changeMonth = (offset) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1);
    // Prevent navigating past current month
    if (newDate <= new Date()) setViewDate(newDate);
  };

  const calendarDays = getCalendarDays();
  const monthName = viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <h1 className="dashboard-title">Welcome back, {user?.name}!</h1>
          <p className="dashboard-subtitle">Here's your productivity overview</p>
        </div>

        {/* Insights Section */}
        <div className="insights-section" style={{ marginTop:-20,marginBottom: 30 }}>
          <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', textAlign: 'center' }}>
            {(() => {
              const insights = getProductivityInsights();
              if (!insights) return 'Start completing tasks to see your productivity insights from the last 7 days!';
              return (
                <>
                  <div>🌟 <b>Your most productive day:</b> {insights.most}</div>
                  <div>😴 <b>Your least productive day:</b> {insights.least}</div>
                  <div>📊 {insights.trendMsg}</div>
                </>
              );
            })()}
          </div>
        </div>
       {/* 🌟 NEW: Side-by-Side Flex Container */}
<div className="dashboard-main-flex" style={{ display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
    
    {/* LEFT SIDE: Consistency Calendar */}
    <div className="consistency-calendar-card compact" style={{ flex: '1', minWidth: '320px', maxWidth: '400px', margin: '0' }}>
        <div className="calendar-header-custom">
            <h3 className="section-title-full-green">Calendar</h3>
            <div className="calendar-nav">
                <button onClick={() => changeMonth(-1)} className="nav-btn">{"<"}</button>
                <span className="month-label-neon" style={{fontSize: '0.8rem'}}>{monthName}</span>
                <button 
                    onClick={() => changeMonth(1)} 
                    className="nav-btn" 
                    disabled={viewDate.getMonth() === new Date().getMonth() && viewDate.getFullYear() === new Date().getFullYear()}
                >
                    {">"}
                </button>
            </div>
        </div>

        <div className="calendar-grid-custom">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                <div key={day} className="weekday-header-small">{day}</div>
            ))}
            
            {calendarDays.map((item, idx) => (
                <div key={idx} className="calendar-day-cell-small">
                    {!item.empty && (
                        <>
                            {item.hasConnection && <div className="streak-connector-neon-small" />}
                            <div 
                                className={`day-dot-small ${item.onTime > 0 ? 'dot-fire' : item.late > 0 ? 'dot-late' : 'dot-empty'}`}
                                data-tooltip-id="cal-tooltip"
                                data-tooltip-content={item.total > 0 ? `${item.onTime} On-time, ${item.late} Late` : `${item.day} ${monthName.split(' ')[0]}: No tasks`}
                            >
                                {item.onTime >= 3 ? '🔥' : item.late > 0 && item.onTime === 0 ? '😴' : ''}
                                <span className="day-number-tiny">{item.day}</span>
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
        <ReactTooltip id="cal-tooltip" place="top" className="extra-z-index" />
    </div>

    {/* RIGHT SIDE: Stats Grid (Now with all 8 stats) */}
    <div className="stats-grid" style={{ flex: '2', minWidth: '350px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', margin: '0' }}>
        <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
                <div className="stat-number">{stats.totalTasks}</div>
                <div className="stat-label">Total Tasks</div>
            </div>
        </div>

        <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
                <div className="stat-number">{stats.completedTasks}</div>
                <div className="stat-label">Completed</div>
            </div>
        </div>

        <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
                <div className="stat-number">{stats.pendingTasks}</div>
                <div className="stat-label">Pending</div>
            </div>
        </div>

        <div className="stat-card">
            <div className="stat-icon">🚨</div>
            <div className="stat-content">
                <div className="stat-number">{stats.overdueTasks}</div>
                <div className="stat-label">Overdue</div>
            </div>
        </div>

        <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
                <div className="stat-number" style={{ color: getCompletionRateColor(stats.completionRate) }}>
                    {stats.completionRate}%
                </div>
                <div className="stat-label">Rate</div>
            </div>
        </div>

        <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-content">
                <div className="stat-number">{stats.currentStreak}</div>
                <div className="stat-label">Streak</div>
            </div>
        </div>

        {/* Added: Average Priority */}
        <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
                <div className="stat-number">{stats.averagePriority}/10</div>
                <div className="stat-label">Avg Priority</div>
            </div>
        </div>

        {/* Added: Completed Today */}
        <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
                <div className="stat-number">{stats.tasksCompletedToday}</div>
                <div className="stat-label">Today</div>
            </div>
        </div>
    </div>
</div>
<style>{`
  /* --- Layout & Container --- */
  .dashboard-main-flex {
    display: flex;
    gap: 30px;
    align-items: flex-start;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 40px;
  }

  /* --- Consistency Calendar Box --- */
  .consistency-calendar-card.compact {
    background: #0a0a0a;
    border: 1px solid #1a1a1a;
    border-radius: 12px;
    padding: 15px;
    flex: 1;
    min-width: 320px;
    max-width: 400px;
    margin: 0;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  }

  .calendar-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .section-title-full-green {
    color: ${NEON_GREEN} !important;
    font-size: 1rem;
    font-weight: 800;
    text-transform: uppercase;
    margin: 0;
  }

  .calendar-nav {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .month-label-neon {
    color: #fff;
    font-weight: 600;
    font-size: 0.85rem;
    min-width: 110px;
    text-align: center;
  }

  .nav-btn {
    background: transparent;
    border: 1px solid #333;
    color: white;
    cursor: pointer;
    border-radius: 4px;
    padding: 2px 8px;
    transition: all 0.2s;
  }

  .nav-btn:hover:not(:disabled) {
    border-color: ${NEON_GREEN};
    background: rgba(0, 255, 136, 0.1);
  }

  .nav-btn:disabled {
    opacity: 0.1;
    cursor: not-allowed;
  }

  /* --- Grid & Day Cells --- */
  .calendar-grid-custom {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
    justify-items: center;
  }

  .weekday-header-small {
    color: #444;
    font-size: 0.7rem;
    font-weight: bold;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  .calendar-day-cell-small {
    position: relative;
    height: 42px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* --- The Dots --- */
  .day-dot-small {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    z-index: 2;
    position: relative;
    transition: transform 0.2s ease;
  }

  .day-dot-small:hover {
    transform: scale(1.2);
    cursor: pointer;
    z-index: 10;
  }

  .dot-fire { 
    background: ${NEON_GREEN}; 
    box-shadow: 0 0 10px ${NEON_GREEN}44;
    color: #000;
  }
  
  .dot-late { 
    background: ${NEON_ORANGE}; 
    box-shadow: 0 0 8px ${NEON_ORANGE}44;
  }

  .dot-empty { 
    background: #111; 
    border: 1px dashed #333;
  }

  .day-number-tiny {
    position: absolute;
    font-size: 0.6rem;
    bottom: -12px;
    width: 100%;
    text-align: center;
    color: #666;
    font-weight: 500;
  }

  /* --- Connection Line --- */
  .streak-connector-neon-small {
    position: absolute;
    width: 45px;
    height: 14px;
    background: ${NEON_GREEN}22;
    left: -22px;
    z-index: 1;
    pointer-events: none;
  }

  /* --- Tooltip Overrides --- */
  .extra-z-index {
    z-index: 9999 !important;
    opacity: 1 !important;
    background-color: #000 !important;
    border: 1px solid ${NEON_GREEN} !important;
    font-size: 0.8rem !important;
    padding: 8px 12px !important;
  }

  /* --- Stats Grid Adjustment --- */
  .stats-grid {
    flex: 2;
    min-width: 350px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 15px;
    margin: 0;
  }
`}</style>
        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-container">
            <h3 className="chart-title">Weekly Productivity (Tasks Completed)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={lineChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={NEON_BG} />
                <XAxis dataKey="name" stroke={NEON_GREEN} />
                <YAxis allowDecimals={false} stroke={NEON_GREEN} />
                <Tooltip contentStyle={{ background: NEON_BG, border: `1px solid ${NEON_GREEN}` }} labelStyle={{ color: NEON_GREEN }} />
                <Line type="monotone" dataKey="Completed" stroke={NEON_GREEN} strokeWidth={3} dot={{ r: 5, fill: NEON_GREEN }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-container">
            <h3 className="chart-title">Completion Rate</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {pieData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={pieColors[idx % pieColors.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: NEON_BG, border: `1px solid ${NEON_GREEN}` }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-container">
            <h3 className="chart-title">Pending Tasks by Importance</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={pendingByImportance} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={NEON_BG} />
                <XAxis dataKey="importance" stroke={NEON_GREEN} type="number" domain={[1, 10]} />
                <YAxis allowDecimals={false} stroke={NEON_GREEN} />
                <Tooltip contentStyle={{ background: NEON_BG, border: `1px solid ${NEON_GREEN}` }} labelStyle={{ color: NEON_GREEN }} />
                <Bar dataKey="tasks" fill={NEON_ORANGE} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-container">
            <h3 className="chart-title">Most Productive 7 Days</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={most7} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={NEON_BG} />
                <XAxis dataKey="formattedDate" stroke={NEON_GREEN} tick={{fontSize: 12}} />
                <YAxis allowDecimals={false} stroke={NEON_GREEN} />
                <Tooltip contentStyle={{ background: NEON_BG, border: `1px solid ${NEON_GREEN}` }} labelStyle={{ color: NEON_GREEN }} />
                <Bar dataKey="count" fill={NEON_GREEN} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-container">
            <h3 className="chart-title">Least Productive 7 Days</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={least7} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={NEON_BG} />
               <XAxis dataKey="formattedDate" stroke={NEON_GREEN} tick={{fontSize: 12}} />
                <YAxis allowDecimals={false} stroke={NEON_GREEN} />
                <Tooltip contentStyle={{ background: NEON_BG, border: `1px solid ${NEON_GREEN}` }} labelStyle={{ color: NEON_GREEN }} />
                <Bar dataKey="count" fill={NEON_PURPLE} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-container">
            <h3 className="chart-title">Weekend vs Weekday Productivity</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={weekendWeekdayPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  <Cell fill={NEON_GREEN} />
                  <Cell fill={NEON_PURPLE} />
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: NEON_BG, border: `1px solid ${NEON_GREEN}` }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ textAlign: 'center', color: 'var(--text-primary)', marginTop: 8, fontWeight: 600 }}>
              {weekendWeekday.weekday > weekendWeekday.weekend
                ? 'You are more productive on weekdays!'
                : weekendWeekday.weekend > weekendWeekday.weekday
                  ? 'You are more productive on weekends!'
                  : 'Your productivity is balanced across the week.'}
            </div>
          </div>
        </div>

        {/* Streak Calendar Section */}
        <div className="calendar-section" style={{ background: 'var(--card-bg)', borderRadius: 16, padding: 24, marginBottom: 40, border: '1px solid var(--card-border)' }}>
          <h3 className="section-title">Streak Tracker</h3>
          <div style={{ width: '100%', maxWidth: '600px', height: '150px' }}>
           <CalendarHeatmap
  startDate={startDate}
  endDate={today}
  values={calendarData}
  classForValue={value => {
    if (!value || value.total === 0) return 'color-empty';
    // If more than 50% of tasks today were late, show the "Late" color
    if (value.late > value.onTime) return 'streak-late';
    // Otherwise show the productive green
    if (value.onTime >= 3) return 'streak-high';
    return 'streak-base';
  }}
  tooltipDataAttrs={value => {
    if (!value || !value.date) return { 'data-tooltip-id': 'heatmap-tooltip', 'data-tooltip-content': 'No activity' };
    
    // 🌟 Decide the Emoji
    const emoji = value.late > 0 && value.onTime === 0 ? '😴' : '🔥';
    const dateLabel = formatXAxisDate(value.date);
    
    return {
      'data-tooltip-id': 'heatmap-tooltip',
      'data-tooltip-content': `${emoji} ${dateLabel}: ${value.onTime} on-time, ${value.late} late`,
    };
  }}
  showWeekdayLabels={true}
  gutterSize={3}
/>
<ReactTooltip 
  id="heatmap-tooltip" 
  style={{ backgroundColor: NEON_BG, border: `1px solid ${NEON_GREEN}`, color: '#fff', borderRadius: '8px', zIndex: 100 }}
/>
  </div>
         <style>{`
  .color-empty { fill: #1a1a1a; }
  
  /* 🔥 Productive Green */
  .streak-base { fill: #008f5d; }
  .streak-high { 
    fill: ${NEON_GREEN};
    filter: drop-shadow(0 0 2px ${NEON_GREEN});
  }
  
  /* 😴 Late/Overdue completion colors */
  .streak-late { fill: ${NEON_ORANGE}; }

  .react-calendar-heatmap rect:hover {
    stroke: #fff;
    stroke-width: 1.5px;
    cursor: pointer;
  }
  
  .react-calendar-heatmap rect {
    transition: fill 0.2s ease;
    rx: 2px; /* Rounded corners like LeetCode */
  }
`}</style>
</div>

        {/* Recent Activity */}
        <div className="recent-activity">
          <h3 className="section-title">Recent Activity</h3>
          <div className="activity-list">
            {tasks
              .filter(task => task.completed && task.completedAt)
              .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
              .slice(0, 5)
              .map((task, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">✅</div>
                  <div className="activity-content">
                    <div className="activity-text">Completed "{task.title}"</div>
                    <div className="activity-time">
                      {formatISTDateTime(task.completedAt)}
                    </div>
                  </div>
                  <div className="activity-priority" style={{ color: getPriorityColor(task.importance) }}>
                    Priority {task.importance}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h3 className="section-title">Quick Actions</h3>
          <div className="actions-grid">
            <button onClick={() => navigate(`/main/${username}`)} className="action-btn">
              <span className="action-icon">📝</span>
              <span>Add New Task</span>
            </button>
            <button onClick={() => navigate(`/main/${username}`)} className="action-btn">
              <span className="action-icon">📊</span>
              <span>View All Tasks</span>
            </button>
            <button onClick={() => navigate('/contact')} className="action-btn">
              <span className="action-icon">💬</span>
              <span>Get Support</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;